import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo } from "react";

import { ExperienceStatus } from "@intelligems/ig-types";
import { Box, Stack, styled } from "@mui/material";
import Grid from "@mui/material/Grid";
import { gridTheme } from "@src/components/MainApp/SharedProductPicker/gridTheme";
import { SelectedProductRenderParams } from "@src/components/MainApp/SharedProductPicker/types";
import { useSelector } from "@src/store";
import {
  areProductPickerSelectedProductsLoading,
  getProductPickerSelectedProducts,
} from "@src/store/slices/productPicker";
import { ProductPickerSelectedProduct } from "@src/store/slices/productPicker/types";

import { ProductListDeleteButton } from "./ProductListDeleteButton";
import { ProductListImage } from "./ProductListImage";
import { ProductListText } from "./ProductListText";

interface SelectedProductListProps {
  isView?: boolean;
  experienceStatus?: ExperienceStatus;
  showToolTipText?: boolean;
}

export const SelectedProductList = (props: SelectedProductListProps) => {
  const selectedProducts = useSelector(getProductPickerSelectedProducts);
  const loading = useSelector(areProductPickerSelectedProductsLoading);
  const columnDefs = useColumnDefs(props);

  return (
    <Stack sx={{ height: "365px" }}>
      <AgGridReact<ProductPickerSelectedProduct>
        theme={theme}
        loading={loading}
        rowData={selectedProducts}
        headerHeight={0}
        columnDefs={columnDefs}
        suppressAggFuncInHeader={true}
        groupDisplayType={"groupRows"}
        rowHeight={60}
        rowSelection={{
          mode: "singleRow",
          enableClickSelection: false,
          checkboxes: false,
        }}
        suppressCellFocus={true}
        cellSelection={false}
        stopEditingWhenCellsLoseFocus={true}
        undoRedoCellEditing={true}
        undoRedoCellEditingLimit={25}
        suppressMenuHide={false}
        enableBrowserTooltips={true}
        suppressHorizontalScroll={true}
      />
    </Stack>
  );
};

function useColumnDefs({ isView, experienceStatus }: SelectedProductListProps) {
  return useMemo((): ColDef<ProductPickerSelectedProduct>[] => {
    const cellRendererParams: SelectedProductRenderParams = {
      isView,
      experienceStatus,
    };
    const defaultDef = {
      cellRendererParams,
      resizable: false,
      suppressHeaderMenuButton: true,
      cellStyle: { display: "flex", alignItems: "center" },
    };
    return [
      {
        ...defaultDef,
        width: 40,
        cellRenderer: ProductListImage,
      },
      {
        ...defaultDef,
        headerName: "Product",
        flex: 1,
        cellRenderer: ProductListText,
      },
      {
        ...defaultDef,
        width: 30,
        minWidth: 30,
        cellRenderer: ProductListDeleteButton,
      },
    ];
  }, [isView, experienceStatus]);
}

const theme = gridTheme.withParams({
  rowBorder: false,
  headerRowBorder: false,
});
